import { Children, Fragment, ReactNode, isValidElement } from "react";
import clsx from "clsx";

import type { ButtonProps } from "../Buttons/Button";
import { describeShortcutHint } from "./modalShortcuts";

interface ModalActionsProps {
  children: ReactNode;
  className?: string;
  /** Set when the modal has no cancel button, so the hint doesn't promise an Esc that does nothing. */
  hideCancelHint?: boolean;
  hideHint?: boolean;
}

const findDefaultAction = (children: ReactNode): ButtonProps | null => {
  let found: ButtonProps | null = null;

  Children.forEach(children, child => {
    if (found || !isValidElement(child)) return;

    const props = child.props as ButtonProps & { children?: ReactNode };
    if (props.isDefaultAction) {
      found = props;
      return;
    }
    if (child.type === Fragment) found = findDefaultAction(props.children);
  });

  return found;
};

/**
 * Footer row for modal buttons. Mark the confirming button with `isDefaultAction` (and
 * optionally a `mnemonic`) and GlobalModalProvider wires Enter / Alt+letter to it; the hint
 * line is derived from that same button so it can't drift out of step with the shortcut.
 */
export const ModalActions: React.FC<ModalActionsProps> = ({
  children,
  className,
  hideCancelHint = false,
  hideHint = false,
}) => {
  const defaultAction = findDefaultAction(children);
  const hint = !hideHint && defaultAction
    ? describeShortcutHint(defaultAction.mnemonic, !hideCancelHint)
    : null;

  return (
    <div className={clsx("mt-6 compact:mt-3", className)}>
      <div className="flex flex-wrap justify-end items-center gap-2">{children}</div>
      {hint && (
        <p className="mt-2 text-right text-xs text-theme-text-muted select-none compact:hidden">{hint}</p>
      )}
    </div>
  );
};

export default ModalActions;
